import React, { useState, useEffect } from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import PersonalDetails from "./PersonalDetails";
import VerificationDetails from "../Loans/VerificationDetails";
import {
  initialPersonalDetails,
  initialVerificationDetails,
  initialNomineeDetails,
  initialOtherDetails,
  initialBankDetails,
} from "../../utils/utils";
import { useLocation } from "react-router-dom";
import NomineeDetails from "./NomineeDetails";
import OtherDetails from "./OtherDetails";
import BankDetails from "./BankDetails";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import { Button, PageHeader, PageLayout } from "../../components/ui";

const steps = [
  "Personal Details",
  "Verification Details",
  "Nominee Details",
  "Other Details",
  "Bank Details",
];

const AddEditCustomer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const queryType = queryParams.get("type");
  const queryStepNo = queryParams.get("stepNo");
  const queryCustomerId = queryParams.get("customerId");

  const stateData = location.state || {};
  const resolvedType = queryType || stateData.type || "Add";
  const [personalDetailsData, setPersonalDetailsData] = useState(
    initialPersonalDetails
  );
  const resolvedCustomerId = queryCustomerId || stateData.customerId || personalDetailsData?.id;
  const initialStep = queryStepNo ? parseInt(queryStepNo) : (stateData.stepNo || 0);

  const [activeStep, setActiveStep] = useState(initialStep);
  const [skipped, setSkipped] = useState(new Set());
  const [verificationDetailsData, setVerificationDetailsData] = useState(
    initialVerificationDetails
  );
  const [nomineeDetailsData, setNomineeDetailsData] = useState(
    initialNomineeDetails
  );
  const [otherDetailsData, setOtherDetailsData] = useState(initialOtherDetails);
  const [bankDetailsData, setBankDetailsData] = useState(initialBankDetails);

  const getCustomersData = async (targetCustomerId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/getModelData/personal_details?filter={"id":${targetCustomerId}}&includeDepth=1&excludeAssociations=transactions,account_details,chit_user`
      );
      const customersData = response.data.data[0];
      if (!customersData) return;
      const personalDetails = {};
      Object.keys(customersData).forEach((key) => {
        if (!key.startsWith("fk_")) {
          personalDetails[key] = customersData[key];
        }
      });
      const verificationDetails =
        customersData.fk_personal_details_hasOne_verification_details_customerId ||
        {};
      const nomineeDetails =
        customersData.fk_personal_details_hasOne_nominee_details_customerId ||
        {};
      const otherDetails =
        customersData.fk_personal_details_hasOne_other_details_customerId || {}
      const bankDetails =
        customersData.fk_personal_details_hasOne_bank_details_customerId || {};

      setPersonalDetailsData(personalDetails);
      setVerificationDetailsData(verificationDetails);
      setNomineeDetailsData(nomineeDetails);
      setOtherDetailsData(otherDetails);
      setBankDetailsData(bankDetails);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch details on mount or customerId change
  useEffect(() => {
    if (resolvedCustomerId) {
      getCustomersData(resolvedCustomerId);
    }
  }, [resolvedCustomerId]);

  // Sync state to URL search parameters for refresh preservation
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const urlType = currentParams.get("type");
    const urlStepNo = currentParams.get("stepNo");
    const urlCustomerId = currentParams.get("customerId");

    const targetCustomerId = resolvedCustomerId ? resolvedCustomerId.toString() : "";
    const targetStepNo = activeStep.toString();
    const targetType = resolvedType;

    if (urlType !== targetType || urlStepNo !== targetStepNo || urlCustomerId !== targetCustomerId) {
      const params = new URLSearchParams();
      if (targetType) params.set("type", targetType);
      params.set("stepNo", targetStepNo);
      if (targetCustomerId) params.set("customerId", targetCustomerId);

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true, state: location.state }
      );
    }
  }, [resolvedType, activeStep, resolvedCustomerId, navigate, location.pathname, location.search]);

  const isStepOptional = (step) => {
    return step === 2 || step === 3 || step === 4;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleStepBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setPersonalDetailsData(initialPersonalDetails);
    setVerificationDetailsData(initialVerificationDetails);
    setNomineeDetailsData(initialNomineeDetails);
    setOtherDetailsData(initialOtherDetails);
    setBankDetailsData(initialBankDetails);
    navigate("/customers");
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PersonalDetails
            personalDetailsData={personalDetailsData}
            setPersonalDetailsData={setPersonalDetailsData}
            verificationDetailsData={verificationDetailsData}
            setVerificationDetailsData={setVerificationDetailsData}
            handleNext={handleNext}
            type={resolvedType}
            mode="customer"
          />
        );
      case 1:
        return (
          <VerificationDetails
            customerId={resolvedCustomerId}
            verificationDetailsData={verificationDetailsData}
            setVerificationDetailsData={setVerificationDetailsData}
            handleNext={handleNext}
            type={resolvedType}
            mode="customer"
          />
        );
      case 2:
        return (
          <NomineeDetails
            customerId={resolvedCustomerId}
            nomineeDetailsData={
              Object.keys(nomineeDetailsData).length === 0
                ? initialNomineeDetails
                : nomineeDetailsData
            }
            setNomineeDetailsData={setNomineeDetailsData}
            handleNext={handleNext}
            type={
              Object.keys(nomineeDetailsData).length === 0
                ? "Add"
                : resolvedType === "View"
                  ? "View"
                  : resolvedType
            }
            mode="customer"
          />
        );
      case 3:
        return (
          <OtherDetails
            customerId={resolvedCustomerId}
            otherDetailsData={otherDetailsData}
            setOtherDetailsData={setOtherDetailsData}
            handleNext={handleNext}
            type={resolvedType}
          />
        );
      case 4:
        return (
          <BankDetails
            customerId={resolvedCustomerId}
            bankDetailsData={
              Object.keys(bankDetailsData).length === 0
                ? initialBankDetails
                : bankDetailsData
            }
            setBankDetailsData={setBankDetailsData}
            handleNext={handleNext}
            type={
              Object.keys(bankDetailsData).length === 0
                ? "Add"
                : resolvedType === "View"
                  ? "View"
                  : resolvedType
            }
            mode="customer"
          />
        );
      default:
        return <p className="text-sm text-ink-700">Unknown Step</p>;
    }
  };

  return (
    <PageLayout
      title={`${resolvedType} Customer`}
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/customers")}
        >
          Back to List
        </Button>
      }
      contentClassName="overflow-hidden flex flex-col gap-2"
    >
      {/* Desktop Stepper */}
      <div className="w-full hidden md:block pb-1">
        <Stepper
          activeStep={activeStep}
          sx={{
            "& .MuiStepIcon-root.Mui-active": { color: "#004BB5" },
            "& .MuiStepIcon-root.Mui-completed": { color: "#10B981" },
            "& .MuiStepLabel-label.Mui-active": { color: "#004BB5", fontWeight: 700 },
            "& .MuiStepLabel-label.Mui-completed": { color: "#10B981" },
          }}
        >
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            if (isStepOptional(index)) {
              labelProps.optional = (
                <span className="text-[10px] text-ink-500 block">Optional</span>
              );
            }
            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step
                key={label}
                {...stepProps}
                onClick={() => {
                  if (resolvedType === "View") {
                    setActiveStep(index);
                  }
                }}
                className={resolvedType === "View" ? "cursor-pointer" : "cursor-default"}
              >
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </div>

      {/* Mobile Automated Stepper - NO SCROLL */}
      <div className="block md:hidden mb-1 px-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold text-ink-500 uppercase">
            STEP {activeStep + 1} OF {steps.length}
          </span>
          <span className="text-[10px] font-extrabold text-primary">
            {Math.round(((activeStep + 1) / steps.length) * 100)}%
          </span>
        </div>

        <div className="h-1 w-full bg-border-light rounded-full mb-2 overflow-hidden">
          <div
            style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            className="h-full bg-primary transition-all duration-300 ease-out"
          />
        </div>

        <div className="flex items-center justify-between p-1.5 bg-white rounded-xl shadow-sm border border-border-light">
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center font-extrabold text-[10px]">
              {activeStep + 1}
            </div>
            <span className="font-semibold text-xs text-ink-900">
              {steps[activeStep]}
            </span>
          </div>
          {activeStep > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleStepBack}
            >
              BACK
            </Button>
          )}
        </div>
      </div>

      {/* Form Scroll Container */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-8">
        {activeStep === steps.length ? (
          <div className="bg-white rounded-xl border border-border-light shadow-sm p-8 max-w-6xl mx-auto mt-6 mb-12">
            <p className="mt-2 mb-1 text-sm text-ink-700">
              All steps completed - you&apos;re finished
            </p>
            <div className="flex flex-row pt-2">
              <div className="flex-1" />
              <Button onClick={handleReset} variant="primary">Add New</Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 mb-1">{renderStepContent(activeStep)}</div>
        )}
      </div>
    </PageLayout>
  );
};

export default AddEditCustomer;
